import { FC, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { usePublicLegacyOperatorDetails } from 'src/apis/organisation-service/use-public-legacy-operator-details';
import { BookingProcessDetails } from 'src/apis/regular-fcfs/types';
import { useBookingProcessDetails } from 'src/apis/regular-fcfs/use-booking-process-details';
import { PageSpinner } from 'src/components/spinner-container';
import { useAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { usePageParamsFromSchema } from 'src/hooks/use-page-params-from-schema';
import { useTitle } from 'src/hooks/use-title';
import { useOrganisationServicePublic } from 'src/pages/settings/organisation/use-organisation-service-public';
import { z } from 'zod';

const Page = lazy(() => import('./page'));

const pageParamsSchema = z.object({
  bookingId: z.string().optional(),
});

export const FcfsBookingDetails: FC = () => {
  useAuthenticatedMonolithUser();
  return <FcfsBookingDetailsProtected />;
};

export const FcfsBookingDetailsProtected: FC = () => {
  const { bookingProcessId } = useParams<{
    bookingProcessId: string;
  }>();

  const pageParams = usePageParamsFromSchema(pageParamsSchema);
  const bookingId = pageParams.value.bookingId;
  useTitle('FCFS Booking');

  const bookingProcessDetails = useBookingProcessDetails(bookingProcessId);
  const organisationService = useOrganisationServicePublic();

  if (!bookingProcessDetails.response || !organisationService.response)
    return <PageSpinner />;

  return (
    <WithOperatorDetails
      bookingProcess={bookingProcessDetails.response.data}
      bookingId={bookingId}
      tsoDetailsUrlTemplate={
        organisationService.response.data._links.getTsoDetails.href
      }
    />
  );
};

const WithOperatorDetails: FC<{
  bookingProcess: BookingProcessDetails;
  bookingId?: string;
  tsoDetailsUrlTemplate: string;
}> = ({ bookingProcess, bookingId, tsoDetailsUrlTemplate }) => {
  const operatorDetails = usePublicLegacyOperatorDetails({
    organisationId: bookingProcess.networkPoint.tso.id,
    url: tsoDetailsUrlTemplate,
  });

  if (!operatorDetails.response) return <PageSpinner />;

  return (
    <Page
      bookingProcess={bookingProcess}
      bookingId={bookingId}
      operatorDetails={operatorDetails.response.data}
    />
  );
};
